import React, { useState, useEffect, useMemo } from 'react';
import { PageLayout } from '../components/layout/PageLayout';
import { clothingService, ClothingItem } from '../services/clothing';
import { Button } from '../components/common/Button';
import { ConfirmModal } from '../components/common/ConfirmModal';
import { EditClothingModal } from '../components/common/EditClothingModal';
import { UploadModal } from '../components/common/UploadModal';
import { getImageUrl } from '../utils/imageUrl';

export const Closet: React.FC = () => {
  const [items, setItems] = useState<ClothingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; itemId: string | null; itemName: string }>({
    isOpen: false,
    itemId: null,
    itemName: '',
  });
  const [editModal, setEditModal] = useState<{ isOpen: boolean; item: ClothingItem | null }>({
    isOpen: false,
    item: null,
  });
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const data = await clothingService.getAll();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load items:', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Group items by category
  const itemsByCategory = useMemo(() => {
    const grouped: { [category: string]: ClothingItem[] } = {};
    items.forEach((item) => {
      const category = item.category?.toLowerCase() || 'other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(item);
    });
    return grouped;
  }, [items]);

  const categories = Object.keys(itemsByCategory).sort();

  const handleDeleteClick = (id: string, category: string) => {
    setDeleteConfirm({
      isOpen: true,
      itemId: id,
      itemName: category,
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.itemId) {
      try {
        await clothingService.delete(deleteConfirm.itemId);
        setItems((prevItems) => (prevItems || []).filter(item => item.id !== deleteConfirm.itemId));
      } catch (err) {
        console.error('Failed to delete item:', err);
      }
    }
    setDeleteConfirm({ isOpen: false, itemId: null, itemName: '' });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, itemId: null, itemName: '' });
  };

  const handleItemClick = (item: ClothingItem) => {
    setEditModal({ isOpen: true, item });
  };

  const handleEditClose = () => {
    setEditModal({ isOpen: false, item: null });
  };

  const handleEditSave = async (id: string, data: {
    category: string;
    subcategory?: string;
    color?: string;
    brand?: string;
    notes?: string;
  }) => {
    await clothingService.update(id, data);
    await loadItems();
  };

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Closet</h1>
          <Button variant="primary" size="md" onClick={() => setUploadModalOpen(true)}>
            Upload Item
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">Your closet is empty!</p>
            <Button onClick={() => setUploadModalOpen(true)}>Upload Your First Item</Button>
          </div>
        ) : (
          <div className="space-y-12">
            {categories.map((category) => (
              <div key={category}>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 capitalize">
                  {category}
                  <span className="text-lg font-normal text-gray-500 ml-2">
                    ({itemsByCategory[category].length})
                  </span>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {itemsByCategory[category].map((item) => (
                    <div
                      key={item.id}
                      className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => handleItemClick(item)}
                    >
                      <div className="aspect-square relative">
                        <img
                          src={getImageUrl(item.image_url)}
                          alt={item.category}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-3">
                        <p className="font-semibold text-sm capitalize">{item.category}</p>
                        {item.color && (
                          <p className="text-xs text-gray-500 capitalize">{item.color}</p>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(item.id, item.category);
                          }}
                          className="mt-2 text-red-500 text-xs hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        title="Delete Clothing Item"
        message={`Are you sure you want to delete this ${deleteConfirm.itemName}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      <EditClothingModal
        isOpen={editModal.isOpen}
        item={editModal.item}
        onClose={handleEditClose}
        onSave={handleEditSave}
      />

      <UploadModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={loadItems}
      />
    </PageLayout>
  );
};

